import { Component } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { finalize, map, Observable } from 'rxjs';
import { NotificationService } from 'src/app/services/notification.service';
import { helper } from 'src/environments/helper';
import { BulevartService } from '../bulevart.service';
import { SaveImageBulevart } from '../bulevart.interface';
import { environment } from 'src/environments/environment';
import { ToastrService } from 'ngx-toastr';
import { Generic } from 'src/app/interfaces/generic.interface';
import { CategoriesService } from '../../bulevart-settings/categories/categories.service';
import { SubcategoriesService } from '../../bulevart-settings/subcategories/subcategories.service';
import { notDefaultValidator } from 'src/app/shared/validators/not-default';
import { TertiaryCategoriesService } from '../../bulevart-settings/tertiary-categories/tertiary-categories.service';

@Component({
    selector: 'app-add-bulevart-image',
    templateUrl: './add-bulevart-image.component.html',
    styleUrls: ['./add-bulevart-image.component.scss']
})
export class AddBulevartImageComponent {
    errorTitle: string = helper.dialogConfig.headers.errorAtentie;
    errorIcon: string = helper.dialogConfig.icons.error;
    errorType: string = helper.dialogConfig.icons.error;

    successTitle: string = helper.dialogConfig.headers.success;
    successIcon: string = helper.dialogConfig.icons.success;
    successType: string = helper.dialogConfig.icons.success;

    helper = helper;

    isLoading = false;
    uploadingImages = false;

    categorii$!: Observable<Generic[]>;
    subcategorii$!: Observable<Generic[]>;
    categoriiTertiare$!: Observable<Generic[]>;

    defaultParameter: { name: string; id: number } = {
        name: '--Alegeți--',
        id: -1,
    };

    form: FormGroup = new FormGroup({
        id_category: new FormControl(-1, [Validators.required, notDefaultValidator('-1')]),
        id_subcategory: new FormControl('', [Validators.required, notDefaultValidator('-1')]),
        id_tertiary_category: new FormControl('', []),
        images: new FormArray([], [Validators.required]),
    });

    constructor(
        private dialogRef: MatDialogRef<AddBulevartImageComponent>,
        private bulevartService: BulevartService,
        private categoriesService: CategoriesService,
        private subcategoriesService: SubcategoriesService,
        private tertiaryCategoriesService: TertiaryCategoriesService,
        private notificationService: NotificationService,
        public toastr: ToastrService,
    ) { }

    closeDialog() {
        this.dialogRef?.close({
            success: false,
        });
    }

    ngOnInit() {
        this.loadCategoriesActive();
    }

    loadCategoriesActive() {
        this.isLoading = true;
        this.categorii$ = this.categoriesService
            .getListActive()
            .pipe(
                map((results: any) => [this.defaultParameter, ...results]),
                finalize(() => (this.isLoading = false))
            );
    }

    onCategorieChange(categorieId: number): void {
        if (categorieId > -1) {
            this.loadSubcategorii(categorieId);
        } else {
            this.subcategorii$ = new Observable<Generic[]>((observer) => {
                observer.next([{ name: '--Alegeți--', id: -1 }]);
                observer.complete();
            });
        }

        this.categoriiTertiare$ = new Observable<Generic[]>((observer) => {
            observer.next([{ name: '--Alegeți--', id: -1 }]);
            observer.complete();
        });

        this.form.get('id_subcategory')?.setValue(-1);
        this.form.get('id_tertiary_category')?.setValue(-1);
    }

    loadSubcategorii(categorieId?: number) {
        this.isLoading = true;
        let subcategoriiObservable$ = categorieId
            ? this.subcategoriesService.getListByCategory(categorieId)
            : this.subcategoriesService.getListActive();

        this.subcategorii$ = subcategoriiObservable$.pipe(
            map((results: any) => [this.defaultParameter, ...results]),
            finalize(() => {
                this.isLoading = false;
            })
        );
    }

    onSubcategorieChange(subcategorieId: number): void {
        if (subcategorieId > -1) {
            this.loadCategoriiTertiare(subcategorieId);
        } else {
            this.categoriiTertiare$ = new Observable<Generic[]>((observer) => {
                observer.next([{ name: '--Alegeți--', id: -1 }]);
                observer.complete();
            });
        }

        this.form.get('id_tertiary_category')?.setValue(-1);
    }

    loadCategoriiTertiare(subcategorieId?: number) {
        this.isLoading = true;
        let tertiareObservable$ = subcategorieId
            ? this.tertiaryCategoriesService.getListBySubcategory(subcategorieId)
            : this.tertiaryCategoriesService.getListActive();

        this.categoriiTertiare$ = tertiareObservable$.pipe(
            map((results: any) => [this.defaultParameter, ...results]),
            finalize(() => {
                this.isLoading = false;
            })
        );
    }

    openToast(object: any) {
        const { type, message, title } = object;

        let options = this.toastr.toastrConfig;
        options.progressBar = true;
        options.preventDuplicates = true;
        options.closeButton = true;

        const inserted = this.toastr.show(
            message,
            title,
            options,
            options.iconClasses[type]
        );
    }

    addImages(event: any) {
        event.preventDefault();
        const inputElement = event.target as HTMLInputElement;
        if (!inputElement.files || inputElement.files.length === 0) {
            return;
        }

        const files = inputElement.files;

        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            this.uploadingImages = true;

            const formData = new FormData();
            formData.append('file', file, file.name);

            this.bulevartService.uploadImage(formData).subscribe({
                next: (result: any) => {
                    let response = typeof result.status_code !== 'undefined' ? result : result.error;
                    if (typeof response.status_code !== 'undefined') {
                        if (
                            response.status_code == 200 &&
                            typeof response.data !== 'undefined'
                        ) {
                            // Add each image file to the images array
                            const images = this.form.get('images') as FormArray;
                            images.push(new FormControl(result.data));

                            this.uploadingImages = false;

                            this.openToast({
                                title: this.successTitle,
                                message: 'Imaginea dvs. a fost adăugată cu succes!',
                                type: this.successIcon,
                            });
                        }
                    } else {
                        // Handle error cases here
                        let errorMessage =
                            environment.config.customNotifications.generalMessages.error;
                        this.notificationService.warningSwal(
                            this.errorTitle,
                            errorMessage,
                            this.errorIcon
                        );
                        this.uploadingImages = false;
                    }
                },
                error: (result: any) => {
                    this.uploadingImages = false;
                    this.onHttpError(result.error);
                },
            });
        }

    }

    onHttpError(res: any, showErrorsInToast: boolean = false) {
        if (res.errors && res.errors.message && res.errors.message.length > 0) {
            res.errors.message.forEach((msg: string) => {
                if (showErrorsInToast) {
                    this.openToast({
                        title: this.errorTitle,
                        message: msg,
                        type: this.errorType,
                    });
                } else {
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                }
            });
        } else {
            let errorMessage =
                environment.config.customNotifications.generalMessages.error;
            this.notificationService.warningSwal(
                this.errorTitle,
                errorMessage,
                this.errorIcon
            );
        }
    }

    removeImage(index: number) {
        const images = this.form.get('images') as FormArray;
        if (index > -1 && index < images.length) {
            images.removeAt(index);
        }
    }

    submitForm() {
        this.isLoading = true;

        const {
            id_category,
            id_subcategory,
            id_tertiary_category,
            images
        } = this.form.value;

        const payload: SaveImageBulevart[] = [];

        for (let i = 0; i < images.length; i++) {
            const image = images[i];
            payload.push({
                original_name: image.original_name,
                filename: image.filename,
                ext: image.ext,
                id_category,
                id_subcategory,
                id_tertiary_category
            })
        }

        this.bulevartService
            .saveImage(payload)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (res) => {
                    this.dialogRef.close({ success: true });
                },
                error: (err) => {
                    let msg;
                    try {
                        // try to catch the message from server
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        // controll output message
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }
}
